import { ReactNode, useEffect } from 'react';
import { openWebUrl } from './web-url-utils';

/**
 * Protocols that can have a meaningful external origin.
 * Only these should be considered for external link interception.
 * All other protocols (mailto:, tel:, sms:, geo:, app deep-links, etc.)
 * should be handled natively by the browser/OS.
 */
const HTTP_PROTOCOLS = new Set(['http:', 'https:']);

/**
 * Checks if a URL is external (different origin from current page).
 * Only considers http: and https: URLs as potentially external.
 * Non-HTTP protocols (mailto:, tel:, sms:, geo:, app deep-links, etc.)
 * are not considered external and should be handled natively by the browser/OS.
 */
export const isExternalUrl = (url: string): boolean => {
    try {
        const linkUrl = new URL(url);

        // Only HTTP/HTTPS URLs can be external; all other protocols
        // (mailto:, tel:, sms:, geo:, custom app schemes, etc.)
        // should be handled natively by the browser/OS
        if (!HTTP_PROTOCOLS.has(linkUrl.protocol)) {
            return false;
        }

        const currentUrl = new URL(window.location.href);
        return linkUrl.origin !== currentUrl.origin;
    } catch {
        return false;
    }
};

/**
 * Finds an anchor element from the event, checking composed path for Shadow DOM support.
 */
const findAnchorFromEvent = (e: MouseEvent): HTMLAnchorElement | null => {
    // Use composedPath to find elements inside Shadow DOM
    const path = e.composedPath();
    for (const element of path) {
        if (element instanceof HTMLAnchorElement) {
            return element;
        }
    }
    return null;
};

/**
 * Gets the absolute href from an anchor element.
 */
const getHrefFromAnchor = (anchor: HTMLAnchorElement | null): string | null => {
    if (anchor?.href) {
        return anchor.href;
    }
    return null;
};

/**
 * Sets up a click handler that intercepts external link clicks.
 * Returns a cleanup function to remove the handler.
 *
 * Uses capture phase to intercept clicks before they reach component handlers,
 * ensuring we can intercept links from UI libraries like Anvil2.
 *
 * @param useInAppBrowser - Whether to open links in in-app browser (mobile context)
 * @returns Cleanup function to remove the event listener
 */
export const setupExternalLinkHandler = (useInAppBrowser?: boolean): (() => void) => {
    const handleClick = (e: MouseEvent) => {
        const anchor = findAnchorFromEvent(e);
        const href = getHrefFromAnchor(anchor);

        // Only intercept external links (different origin)
        if (href && isExternalUrl(href)) {
            e.preventDefault();
            e.stopPropagation();
            openWebUrl(href, useInAppBrowser);
        }
    };

    // Use capture phase to intercept before component handlers
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
};

interface ExternalLinkHandlerProps {
    children: ReactNode;
    /** Whether to open external links in in-app browser (mobile context) */
    useInAppBrowser?: boolean;
}

/**
 * Intercepts all external anchor tag clicks and routes them through openWebUrl.
 * Works with Shadow DOM (web components) by using composedPath.
 */
export const ExternalLinkHandler = ({ children, useInAppBrowser }: ExternalLinkHandlerProps) => {
    useEffect(() => {
        return setupExternalLinkHandler(useInAppBrowser);
    }, [useInAppBrowser]);

    return children;
};
